// Platform connections: the agency emails, their health, and their credentials.

import { Fragment, useCallback, useEffect, useState } from 'react';
import { reachApi } from '../api.js';
import { formatConnectionDate } from '../format.js';
import { ActionMenu, ButtonSpinner, LoadingState, PLATFORMS } from '../components/ui.jsx';
import { liveAccountLabel, useAdAccounts } from '../hooks.js';

// One row per platform credential this client has stored. The rows come from bp_credentials
// via the backend, which proxies to the batch layer — no token value is ever sent to this app,
// so everything rendered here is status, expiry and platform.
//
// The agency's platform connections — roughly seven email accounts, not one per client.
// Clients grant these emails access to their ad accounts, so a single row here reaches many
// clients' data. That is also why every endpoint behind this screen is admin-only: the ad
// accounts a connection can see span clients, and a client-scoped user must not browse them.
// The agency's platform connections — roughly seven email accounts, not one per client.
// Clients grant these emails access to their ad accounts, so a single row here reaches many
// clients' data. That is also why every endpoint behind this screen is admin-only: the ad
// accounts a connection can see span clients, and a client-scoped user must not browse them.
//
// A connection is three values that travel together, because there is one Meta app per email:
// the app id, that app's secret, and an access token issued by that app.
export function ConnectionsView() {
  const [rows, setRows] = useState(null);      // null = not loaded yet; [] = loaded and empty
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');        // id (or 'new') of whatever is mid-action
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_CONNECTION_DRAFT);
  const [notice, setNotice] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setRows(await reachApi.listConnections());
    } catch (err) {
      setRows([]);
      // The common failure here is not a bug: a client-scoped user has no business on this
      // screen, and the API says so. Passing the message through beats inventing one.
      setError(err?.message || 'Connections could not be loaded.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy('new'); setError(''); setNotice('');
    try {
      const result = await reachApi.connect({
        appId: draft.appId.trim(),
        appSecret: draft.appSecret.trim(),
        token: draft.token.trim(),
        platform: draft.platform,
        exchange: draft.exchange,
        label: draft.label.trim(),
        connectionId: draft.connectionId || '',
      });
      setNotice(result.warning
        || `Connected ${result.label}. ${result.accounts?.length || 0} ad account(s) visible.`);
      setAdding(false);
      setDraft(EMPTY_CONNECTION_DRAFT);
      await load();
    } catch (err) {
      setError(err?.message || 'The connection could not be stored.');
    } finally {
      setBusy('');
    }
  };

  // The shared shape of every per-row action: one call, its outcome as a notice or the
  // server's own message as the error, and a reload either way.
  const rowAction = async (row, fn, describe) => {
    setBusy(row.id); setError(''); setNotice('');
    try {
      const result = await fn();
      setNotice(describe(result));
      await load();
    } catch (err) {
      setError(err?.message || 'The connection could not be changed.');
    } finally {
      setBusy('');
    }
  };

  const disconnect = (row) => rowAction(row, () => reachApi.disconnect(row.id),
    // Revoking does not delete the streams pointing here, and saying how many it stopped is
    // the difference between an informed action and a surprise tomorrow morning.
    result => result.streams_affected
      ? `${row.label} disconnected. ${result.streams_affected} enabled stream(s) will not run until it is reconnected.`
      : `${row.label} disconnected.`);

  const testConnection = (row) => rowAction(row, () => reachApi.testConnection(row.id),
    result => result.ok
      ? `${row.label} works — ${result.accounts_visible} ad account(s) visible.`
      : `${row.label} failed its test: ${result.error}`);

  const reactivate = (row) => rowAction(row, () => reachApi.reactivateConnection(row.id),
    () => `${row.label} is active again.`);

  const removeConnection = (row) => rowAction(row, () => reachApi.deleteConnection(row.id),
    () => `${row.label} deleted.`);

  const reconnect = (row) => {
    // Pre-filled with the app id, blank for the secret and token: reconnecting almost always
    // means the same app with a fresh token, and re-typing an app id to change a token is
    // pointless friction. The secret is never sent back to this screen, so it cannot be
    // prefilled even in principle. connectionId pins the update to THIS row — see api.connect.
    setDraft({ ...EMPTY_CONNECTION_DRAFT, platform: row.platform, appId: row.app_id || '', label: row.label, connectionId: row.id });
    setAdding(true);
  };

  // The expiry warning the schema indexed for and nothing ever asked: connections dying
  // within a fortnight, surfaced before they take their streams down with them.
  const expiringSoon = (rows || []).filter(row => {
    if (row.status !== 'active' || !row.expires_at) return false;
    const days = (new Date(row.expires_at) - Date.now()) / 86400000;
    return days >= 0 && days <= 14;
  });

  return <div>
    <div className="o-head-row">
      <div>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Connections</h2>
        <p className="o-note" style={{ margin: 0 }}>Manage the platform credentials Reach uses to discover accounts and run streams.</p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label="Refresh connections" title="Refresh connections" onClick={load}>↻</button>
        <button type="button" className="o-btn o-btn-primary" onClick={() => { setDraft(EMPTY_CONNECTION_DRAFT); setAdding(true); }}>Add connection</button>
      </div>
    </div>

    {error && <div className="o-state o-error" style={{ marginBottom: 16 }}>{error}</div>}
    {notice && <div className="o-hint" style={{ marginBottom: 16 }}>{notice}</div>}
    {expiringSoon.length > 0 && (
      <div className="o-hint" style={{ marginBottom: 16 }}>
        <b>Expiring within two weeks:</b>{' '}
        {expiringSoon.map(row => `${row.label} (${formatConnectionDate(row.expires_at)})`).join(', ')}.
        {' '}Reconnect them before their streams start failing overnight.
      </div>
    )}

    {rows === null
      ? <LoadingState title="Loading connections…" detail="Checking connection health and stream usage." />
      : rows.length === 0
        ? <div className="o-hint"><b>No platform connections yet.</b> Add credentials for Facebook, LinkedIn, Snapchat or TikTok to discover available ad accounts.</div>
        : <div className="o-panel" style={{ overflowX: 'auto' }}>
            <table className="o-tbl" style={{ width: '100%', minWidth: 1020 }}>
              <thead><tr><th>Connection</th><th>Platform</th><th>App</th><th>Status</th><th>Token expires</th><th>Streams</th><th>Actions</th></tr></thead>
              <tbody>
                {rows.map(row => {
                  const active = row.status === 'active';
                  const revoked = row.status === 'revoked';
                  const streamCount = Number(row.stream_count || 0);
                  return <Fragment key={row.id}>
                    <tr>
                      <td className="o-strong">{row.label}</td>
                      <td>{PLATFORMS.find(item => item.id === row.platform)?.label || row.platform}</td>
                      {/* The app id is not a secret — it appears in Meta's own consent URLs —
                          and showing it is how an admin confirms which of the seven apps this
                          connection uses. */}
                      <td><span className="o-code">{row.app_id || 'not set'}</span></td>
                      <td><span className={`o-badge ${active ? 'o-ok' : 'o-err'}`}>{active ? 'Connected' : row.status === 'expired' ? 'Needs reconnecting' : 'Disconnected'}</span></td>
                      {/* No expiry is not unknown — a system-user token genuinely never expires,
                          and saying so is more useful than an em dash. */}
                      <td className="o-num">{row.expires_at ? formatConnectionDate(row.expires_at) : 'Never'}</td>
                      {/* What a disconnect or delete would touch — visible before the click,
                          not in the toast afterwards. */}
                      <td className="o-num">{streamCount}</td>
                      <td className="o-acts">
                        <button type="button" className="o-btn o-btn-sm" onClick={() => setExpanded(expanded === row.id ? null : row.id)} aria-expanded={expanded === row.id}>{expanded === row.id ? 'Hide accounts' : 'View accounts'}</button>
                        <ActionMenu label={`More actions for ${row.label}`}>
                          <button type="button" className="o-btn o-btn-sm" disabled={busy === row.id} onClick={() => testConnection(row)}>{busy === row.id && <ButtonSpinner />}Test connection</button>
                          <button type="button" className="o-btn o-btn-sm" disabled={busy === row.id} onClick={() => reconnect(row)}>Reconnect</button>
                          {revoked && <button type="button" className="o-btn o-btn-sm" disabled={busy === row.id} onClick={() => reactivate(row)}>Reactivate</button>}
                          {active && <button type="button" className="o-btn o-btn-danger o-btn-sm" disabled={busy === row.id} onClick={() => disconnect(row)}>Disconnect</button>}
                        {/* Delete only offers itself when it can succeed: the server refuses
                            while streams reference the row, so a button that would always
                            bounce is noise, not an affordance. */}
                          {!active && streamCount === 0 && <button type="button" className="o-btn o-btn-danger o-btn-sm" disabled={busy === row.id} onClick={() => removeConnection(row)}>Delete</button>}
                        </ActionMenu>
                      </td>
                    </tr>
                    {expanded === row.id && <tr>
                      <td colSpan={7} style={{ background: 'var(--surface-2)' }}>
                        <ConnectionAccounts connectionId={row.id} />
                      </td>
                    </tr>}
                  </Fragment>;
                })}
              </tbody>
            </table>
          </div>}

    {adding && <div className="o-scrim" role="dialog" aria-modal="true" onClick={() => setAdding(false)}>
      <form className="o-modal" onSubmit={event => { event.preventDefault(); connect(); }} onClick={event => event.stopPropagation()}>
        <div className="o-modal-head"><h2 style={{ margin: 0, fontSize: 18 }}>{draft.connectionId ? 'Reconnect platform' : 'Add connection'}</h2></div>
        <div className="o-modal-body" style={{ display: 'grid', gap: 16 }}>
          <div className="o-hint"><b>One platform app per connection.</b> Paste the app id and secret together with the token described below. Secrets and tokens are encrypted and never returned to this screen.</div>
          <label>
            <span className="o-field-lbl">Platform</span>
            {/* Only platforms whose credential handling and extractor are both registered. */}
            <select className="o-control" value={draft.platform} onChange={event => setDraft(current => ({ ...current, platform: event.target.value }))} style={{ marginTop: 8 }}>
              {CONNECTABLE_PLATFORMS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span className="o-field-lbl">App id</span>
            <input className="o-control" value={draft.appId} onChange={event => setDraft(current => ({ ...current, appId: event.target.value }))} style={{ marginTop: 8 }} autoFocus />
          </label>
          <label>
            <span className="o-field-lbl">App secret</span>
            <input className="o-control" type="password" value={draft.appSecret} onChange={event => setDraft(current => ({ ...current, appSecret: event.target.value }))} style={{ marginTop: 8 }} />
          </label>
          <label>
            <span className="o-field-lbl">{authFor(draft.platform).tokenLabel}</span>
            <textarea className="o-control" rows={3} value={draft.token} onChange={event => setDraft(current => ({ ...current, token: event.target.value }))} style={{ marginTop: 8, resize: 'vertical' }} />
            {authFor(draft.platform).tokenHint && <span className="o-note" style={{ display: 'block', marginTop: 8 }}>{authFor(draft.platform).tokenHint}</span>}
          </label>
          {/* Only Meta has a "do not exchange this" case. LinkedIn always exchanges — a refresh
              token that cannot mint an access token is not a credential, and finding that out
              now beats finding it out on the first overnight run. */}
          {draft.platform === 'facebook' && <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={!draft.exchange} onChange={event => setDraft(current => ({ ...current, exchange: !event.target.checked }))} />
            <span>This is a system-user token (never expires, do not exchange it)</span>
          </label>}
          {(authFor(draft.platform).labelRequired || !draft.exchange) && <label>
            <span className="o-field-lbl">Email for this connection</span>
            <input className="o-control" type="email" value={draft.label} onChange={event => setDraft(current => ({ ...current, label: event.target.value }))} style={{ marginTop: 8 }} placeholder="ops@agency.com" />
            <span className="o-note" style={{ display: 'block', marginTop: 8 }}>{authFor(draft.platform).labelRequired
              ? 'This platform cannot report which account authorised the credentials, so this is the only name the connection will have.'
              : 'A system-user token has no mailbox, so no address can be reported for it. For ordinary tokens the platform is asked and anything typed here is ignored.'}</span>
          </label>}
        </div>
        <div className="o-modal-foot">
          <button type="button" className="o-btn" onClick={() => setAdding(false)}>Cancel</button>
          <button type="submit" className="o-btn o-btn-primary" disabled={busy === 'new' || !draft.appId.trim() || !draft.appSecret.trim() || !draft.token.trim() || ((authFor(draft.platform).labelRequired || !draft.exchange) && !draft.label.trim())}>{busy === 'new' && <ButtonSpinner />}{busy === 'new' ? 'Validating and connecting…' : draft.connectionId ? 'Reconnect' : 'Add connection'}</button>
        </div>
      </form>
    </div>}
  </div>;
}


export const EMPTY_CONNECTION_DRAFT = { platform: 'facebook', appId: '', appSecret: '', token: '', label: '', exchange: true, connectionId: '' };


// What a platform calls the third credential, and whether it can tell us who authorised it.
//
// The difference is not cosmetic. Meta stores an access token and reports the account's email;
// LinkedIn stores a REFRESH token (access tokens are minted from it per extraction) and its
// Marketing API apps cannot read a profile at all, so the label an admin types is the only name
// that connection will ever have. Asking for it up front beats storing a row called "unknown" —
// which is exactly what the one existing Meta connection ended up called.
export const PLATFORM_AUTH = {
  facebook: {
    tokenLabel: 'Access token',
    tokenHint: 'A long-lived or system-user token. A short-lived one is exchanged before storing.',
    labelRequired: false,
  },
  linkedin: {
    tokenLabel: 'Refresh token',
    tokenHint: 'LinkedIn issues refresh tokens (~365 days). Access tokens are minted from it for each extraction and never stored.',
    labelRequired: true,
  },
  snapchat: {
    tokenLabel: 'Refresh token',
    tokenHint: 'Use the OAuth refresh token. Reach exchanges it for a short-lived access token whenever it extracts.',
    labelRequired: true,
  },
  tiktok: {
    tokenLabel: 'Access token',
    tokenHint: 'Use the long-lived access token returned by TikTok API for Business OAuth.',
    labelRequired: true,
  },
};


// The platforms an email can actually be connected on: the ones with credential handling
// behind them (PLATFORM_AUTH mirrors the server's registry). Kept separate from PLATFORMS —
// the stream wizard and connection screen therefore stay in sync.
//
// AFTER PLATFORM_AUTH by necessity, not style: this initializer runs eagerly at module
// evaluation, and a module-level const referenced before its declaration is a temporal-dead-
// zone ReferenceError that took the whole SPA down — a class of bug the bundler cannot see,
// which is exactly why the browser pass exists.
export const CONNECTABLE_PLATFORMS = PLATFORMS.filter(item => PLATFORM_AUTH[item.id]);


export const authFor = platform => PLATFORM_AUTH[platform] || {
  tokenLabel: 'Access token', tokenHint: '', labelRequired: true,
};


// What one connection can currently see, read on demand rather than with the row list: it is a
// live platform call per connection, and expanding seven of them on page load would make the
// screen wait on seven round trips nobody asked for.
export function ConnectionAccounts({ connectionId }) {
  const { loading, accounts, error } = useAdAccounts(connectionId);

  if (loading) return <LoadingState title="Loading ad accounts…" detail="Reading the account names and IDs available through this connection." style={{ border: 0, maxWidth: 'none' }} />;
  if (error) return <div className="o-note" style={{ padding: 12 }}>{error}</div>;
  if (!accounts.length) return <div className="o-note" style={{ padding: 12 }}>This email can see no ad accounts. Check that clients granted it access in Business Manager.</div>;

  return <div style={{ padding: 12 }}>
    <p className="o-note" style={{ margin: '0 0 8px' }}>{accounts.length} ad account(s), across every client that granted this email access.</p>
    <div className="o-chipset">
      {accounts.map(account => <span key={account.external_id} className="o-code">{liveAccountLabel(account)}</span>)}
    </div>
  </div>;
}

